import { isoWeek } from "./isoWeek";

/**
 * The Hall of Records (MP-27, docs/MODES.md §8): personal bests kept on the device, with no ranking server — the
 * `records` save section. It keeps what no other section does: the Endless best overall and this ISO week, the fastest
 * clear of each level (campaign and farm runs, in sim milliseconds), each Race Week's best time and medal, and the
 * challenge codes played with their bests. Iron stars, level stars and the Daily Road days are read live from their own
 * sections, never copied. Filled from the app's game events (battleWon, raceFinished), each counted once by its key,
 * plus a one-time backfill of the Endless bests the `modes` section already held.
 */

export type RaceMedal = "" | "bronze" | "silver" | "gold";
/** A medal's rank: gold highest; "" (finished under bronze) lowest. */
const MEDAL_RANK: Readonly<Record<RaceMedal, number>> = { "": 0, bronze: 1, silver: 2, gold: 3 };
export const RACE_MEDALS: readonly Exclude<RaceMedal, "">[] = ["gold", "silver", "bronze"];

/** The run modes whose clear times are kept per level. */
export type ClearMode = "campaign" | "farm";
export const CLEAR_MODES: readonly ClearMode[] = ["campaign", "farm"];

export interface EndlessBest { readonly waves: number; readonly points: number }
export interface EndlessWeekBest extends EndlessBest { readonly week: string }
/** A level's fastest clears (sim ms) by mode. */
export type ClearTimes = Readonly<Partial<Record<ClearMode, number>>>;
export interface RaceWeekRecord { readonly week: string; readonly level: number; readonly ms: number; readonly medal: RaceMedal }
/** A challenge code's best run, and how often it was played. */
export interface ChallengeRecord {
  readonly code: string;
  readonly level: number;
  readonly won: boolean;
  /** The best win's clear time (sim ms); 0 while unwon. */
  readonly ms: number;
  /** Waves cleared in the best run. */
  readonly stages: number;
  readonly runs: number;
  /** When it was last played (clock seconds). */
  readonly at: number;
}

/** A finished challenge run as the Hall records it. */
export interface ChallengeResult { readonly code: string; readonly level: number; readonly won: boolean; readonly ms: number; readonly stages: number; readonly key: string; readonly at: number }

/** What a recorded result did: counted at all (false for a key seen before), beat the best, and the best before it. */
export interface RecordNote<T> { readonly counted: boolean; readonly newBest: boolean; readonly previous: T | null }

export interface RecordsJson {
  v: number;
  backfilled: boolean;
  endless: EndlessBest;
  endlessWeek: EndlessWeekBest;
  clears: Record<string, ClearTimes>;
  races: RaceWeekRecord[];
  challenges: ChallengeRecord[];
  keys: string[];
}

const RECORDS_VERSION = 1;
const NO_ENDLESS: EndlessBest = { waves: 0, points: 0 };

/** "2026-W42": the ISO week of a UTC day, as Race Week names its weeks. */
export function isoWeekKey(day: number): string {
  const [y, w] = isoWeek(day);
  return `${y}-W${w < 10 ? "0" : ""}${w}`;
}

const isInt = (v: unknown): v is number => Number.isInteger(v);
const nonNeg = (v: unknown): number => (isInt(v) && v >= 0 ? v : 0);
const isWeek = (v: unknown): v is string => typeof v === "string" && /^\d{4}-W\d{2}$/.test(v);
const betterEndless = (a: EndlessBest, b: EndlessBest): boolean => a.waves > b.waves || (a.waves === b.waves && a.points > b.points);
/** A better challenge run: a win over a loss, then the faster win, then more waves. */
const betterChallenge = (a: { won: boolean; ms: number; stages: number }, b: ChallengeRecord): boolean =>
  a.won !== b.won ? a.won : a.won ? a.ms < b.ms : a.stages > b.stages;

export class RecordsState {
  backfilled = false;
  private endlessAll: EndlessBest = NO_ENDLESS;
  private endlessWk: EndlessWeekBest = { week: "", ...NO_ENDLESS };
  private readonly clearTimes = new Map<number, ClearTimes>();
  private raceWeeks: RaceWeekRecord[] = [];
  private codes: ChallengeRecord[] = [];
  private keys: string[] = [];

  /** Remembers `key`; false when it was seen (the event is counted already). */
  private fresh(key: string, kept: number): boolean {
    if (this.keys.includes(key)) return false;
    this.keys = [key, ...this.keys].slice(0, Math.max(1, kept));
    return true;
  }

  // ---- Endless
  get endlessBest(): EndlessBest { return this.endlessAll; }
  /** This ISO week's best: none once the stored week is over. */
  endlessWeekBest(week: string): EndlessBest { return this.endlessWk.week === week ? this.endlessWk : NO_ENDLESS; }
  /** An Endless score of a week no longer running: the overall best only. */
  noteEndlessOverall(score: EndlessBest): RecordNote<EndlessBest> {
    const before = this.endlessAll, newBest = betterEndless(score, before);
    if (newBest) this.endlessAll = { waves: score.waves, points: score.points };
    return { counted: true, newBest, previous: before.waves > 0 ? before : null };
  }
  /** An Endless score of ISO week `week`: raises the overall and the week's best (a new week starts from it). */
  noteEndless(week: string, score: EndlessBest): RecordNote<EndlessBest> {
    const before = this.endlessAll;
    if (betterEndless(score, before)) this.endlessAll = { waves: score.waves, points: score.points };
    // a later week starts afresh; an earlier one (a clock set back) only counts toward the overall best
    const weekBefore = this.endlessWeekBest(week);
    if (week > this.endlessWk.week || (week === this.endlessWk.week && betterEndless(score, weekBefore))) {
      this.endlessWk = { week, ...(betterEndless(score, weekBefore) ? { waves: score.waves, points: score.points } : weekBefore) };
    }
    return { counted: true, newBest: betterEndless(score, before), previous: before.waves > 0 ? before : null };
  }

  // ---- clear times
  clearsOf(level: number): ClearTimes { return this.clearTimes.get(level) ?? {}; }
  /** Levels with a clear time, ascending. */
  get clearedLevels(): number[] { return [...this.clearTimes.keys()].sort((a, b) => a - b); }
  /** A won battle (the battleWon event): a campaign or farm win may set the level's fastest clear. Counted once per key. */
  noteClear(mode: string, level: number, ms: number, key: string, keysKept: number): RecordNote<number> {
    const m = CLEAR_MODES.find((x) => x === mode);
    if (!m || !isInt(level) || level < 1 || !isInt(ms) || ms <= 0) return { counted: false, newBest: false, previous: null };
    const before = this.clearsOf(level)[m] ?? null;
    if (!this.fresh(key, keysKept)) return { counted: false, newBest: false, previous: before };
    const newBest = before === null || ms < before;
    if (newBest) this.clearTimes.set(level, { ...this.clearsOf(level), [m]: ms });
    return { counted: true, newBest, previous: before };
  }

  // ---- Race Week
  get races(): readonly RaceWeekRecord[] { return this.raceWeeks; }
  /** Weeks by their best medal (a week counts once, at its best). */
  medalCounts(): Record<Exclude<RaceMedal, "">, number> {
    const out = { gold: 0, silver: 0, bronze: 0 };
    for (const r of this.raceWeeks) if (r.medal !== "") out[r.medal]++;
    return out;
  }
  /** A finished Race Week run (raceFinished): the week's best time and its best medal. Counted once per key. */
  noteRace(r: { readonly week: string; readonly level: number; readonly ms: number; readonly medal: RaceMedal; readonly key: string }, keysKept: number, weeksKept: number): RecordNote<RaceWeekRecord> {
    if (!isWeek(r.week) || !isInt(r.ms) || r.ms <= 0 || !(r.medal in MEDAL_RANK)) return { counted: false, newBest: false, previous: null };
    const before = this.raceWeeks.find((x) => x.week === r.week) ?? null;
    if (!this.fresh(r.key, keysKept)) return { counted: false, newBest: false, previous: before };
    const newBest = before === null || r.ms < before.ms;
    const medal = before && MEDAL_RANK[before.medal] > MEDAL_RANK[r.medal] ? before.medal : r.medal;
    const next: RaceWeekRecord = { week: r.week, level: newBest ? r.level : before!.level, ms: newBest ? r.ms : before!.ms, medal };
    this.raceWeeks = [next, ...this.raceWeeks.filter((x) => x.week !== r.week)].sort((a, b) => (a.week < b.week ? 1 : -1)).slice(0, Math.max(1, weeksKept));
    return { counted: true, newBest, previous: before };
  }

  // ---- challenge codes
  /** The codes played, most recent first. */
  get challenges(): readonly ChallengeRecord[] { return this.codes; }
  challenge(code: string): ChallengeRecord | null { return this.codes.find((c) => c.code === code) ?? null; }
  /** A finished challenge run: the code's best (a win, then the fastest; else the most waves), moved to the front. */
  noteChallenge(r: ChallengeResult, recentKept: number, keysKept: number): RecordNote<ChallengeRecord> {
    const before = this.challenge(r.code);
    if (!this.fresh(r.key, keysKept)) return { counted: false, newBest: false, previous: before };
    const run = { won: r.won, ms: r.won ? Math.max(0, r.ms) : 0, stages: Math.max(0, r.stages) };
    const newBest = before === null || betterChallenge(run, before);
    const best = newBest ? run : before!;
    const next: ChallengeRecord = { code: r.code, level: r.level, ...best, runs: (before?.runs ?? 0) + 1, at: r.at };
    this.codes = [next, ...this.codes.filter((c) => c.code !== r.code)].slice(0, Math.max(1, recentKept));
    return { counted: true, newBest, previous: before };
  }

  /** The one-time backfill from what the save held before the Hall: the Endless bests (`modes` section). */
  backfill(endless: EndlessBest, week: EndlessWeekBest | null): boolean {
    if (this.backfilled) return false;
    this.backfilled = true;
    this.noteEndlessOverall(endless);
    if (week && isWeek(week.week)) this.noteEndless(week.week, week);
    return true;
  }

  /** Whether anything is kept here (the Hall's other rows are read live). */
  get hasAny(): boolean { return this.endlessAll.waves > 0 || this.clearTimes.size > 0 || this.raceWeeks.length > 0 || this.codes.length > 0; }

  toJson(): RecordsJson {
    const clears: Record<string, ClearTimes> = {};
    for (const l of this.clearedLevels) clears[String(l)] = { ...this.clearsOf(l) };
    return {
      v: RECORDS_VERSION, backfilled: this.backfilled, endless: { ...this.endlessAll }, endlessWeek: { ...this.endlessWk }, clears,
      races: this.raceWeeks.map((r) => ({ ...r })), challenges: this.codes.map((c) => ({ ...c })), keys: [...this.keys],
    };
  }

  static fromJson(j: unknown): RecordsState {
    const s = new RecordsState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<RecordsJson>;
    s.backfilled = src.backfilled === true;
    const e = src.endless, w = src.endlessWeek;
    if (e) s.endlessAll = { waves: nonNeg(e.waves), points: nonNeg(e.points) };
    if (w && isWeek(w.week)) s.endlessWk = { week: w.week, waves: nonNeg(w.waves), points: nonNeg(w.points) };
    for (const [k, t] of Object.entries(typeof src.clears === "object" && src.clears !== null ? src.clears : {})) {
      if (!/^\d+$/.test(k) || Number(k) < 1 || typeof t !== "object" || t === null) continue;
      const times: Partial<Record<ClearMode, number>> = {};
      for (const m of CLEAR_MODES) if (isInt(t[m]) && t[m]! > 0) times[m] = t[m];
      if (Object.keys(times).length > 0) s.clearTimes.set(Number(k), times);
    }
    for (const r of Array.isArray(src.races) ? src.races : []) {
      if (typeof r !== "object" || r === null || !isWeek(r.week) || !(r.medal in MEDAL_RANK) || s.raceWeeks.some((x) => x.week === r.week)) continue;
      s.raceWeeks.push({ week: r.week, level: nonNeg(r.level), ms: nonNeg(r.ms), medal: r.medal });
    }
    s.raceWeeks.sort((a, b) => (a.week < b.week ? 1 : -1));
    for (const c of Array.isArray(src.challenges) ? src.challenges : []) {
      if (typeof c !== "object" || c === null || typeof c.code !== "string" || s.codes.some((x) => x.code === c.code)) continue;
      s.codes.push({ code: c.code, level: nonNeg(c.level), won: c.won === true, ms: nonNeg(c.ms), stages: nonNeg(c.stages), runs: Math.max(1, nonNeg(c.runs)), at: nonNeg(c.at) });
    }
    s.keys = Array.isArray(src.keys) ? src.keys.filter((k): k is string => typeof k === "string") : [];
    return s;
  }
}
